// ABOUTME: Responsive sidebar component with URL routing navigation
// ABOUTME: Adapts between desktop sidebar and mobile overlay based on screen size

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Sidebar, SidebarContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Home, FileText, Settings, LogOut, User, CreditCard, DollarSign } from 'lucide-react';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useIsMobile } from '@/hooks/use-mobile';

interface AppSidebarProps {
  user: any;
  profile: any;
  onSignOut: () => Promise<void>;
}

const AppSidebar = ({
  user,
  profile,
  onSignOut
}: AppSidebarProps) => {
  const { isAdmin } = useUserRoles();
  const isMobile = useIsMobile();
  const location = useLocation();

  const referralItems = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: Home,
    path: '/'
  }, {
    id: 'submit',
    label: 'Role Hub',
    icon: FileText,
    path: '/role-hub'
  }, {
    id: 'public-profile',
    label: 'My Profile',
    icon: User,
    path: '/my-profile'
  }];

  const hrItems = [{
    id: 'hr-dashboard',
    label: 'Dashboard',
    icon: Home,
    path: '/hr'
  }, {
    id: 'manage-roles',
    label: 'Manage Roles',
    icon: Settings,
    path: '/manage-roles'
  }];

  // Calculate display name
  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) {
      return profile.first_name;
    }
    if (user?.email) {
      return user.email.split('@')[0]; // Show username part on mobile
    }
    return 'User';
  };

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Sidebar 
      className="bg-white border-r border-gray-200"
      collapsible={isMobile ? "offcanvas" : "icon"}
    >
      <SidebarHeader className={`p-4 ${isMobile ? 'p-3' : 'p-6'}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">S</span>
          </div>
          <div className="min-w-0">
            <h1 className={`font-bold text-gray-900 truncate ${isMobile ? 'text-lg' : 'text-xl'}`}>
              Sourcerer AI
            </h1>
            <p className="text-xs text-gray-500 truncate">ReferralHub Pro</p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 pb-2">
        {/* Referral Section */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-sm font-semibold text-gray-600 mb-2 px-2">
            Referral Section
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {referralItems.map(item => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton asChild isActive={isActivePath(item.path)}>
                    <NavLink 
                      to={item.path}
                      className="w-full justify-start text-black hover:bg-gray-100 active:bg-gray-200 rounded-lg mx-1 flex items-center gap-2 px-2 py-2"
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* HR Section */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-sm font-semibold text-gray-600 mb-2 mt-4 px-2">
            HR Section
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {hrItems.map(item => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton asChild isActive={isActivePath(item.path)}>
                    <NavLink 
                      to={item.path}
                      className="w-full justify-start text-black hover:bg-gray-100 active:bg-gray-200 rounded-lg mx-1 flex items-center gap-2 px-2 py-2"
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="flex items-center gap-2 px-2 w-full justify-start text-black hover:bg-gray-100 h-auto py-2"
            >
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4" />
              </div>
              <span className="font-medium truncate text-sm">{getDisplayName()}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem asChild className="text-black">
              <NavLink to="/my-profile" className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                My Profile
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem className="text-black">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onSignOut} className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
